import { Component } from 'react';
import Pusher from 'pusher-js';
import CONFIG, { getEnv } from '../../config';
import { flatten, get, unset } from 'lodash';
import { Equipment } from '../interfaces/equipment';
import { addToMap, clearEquipMap } from '../../dashboard/dashboard.actions';
import { Store } from 'redux';
import { getAllDeviceIds } from '../../utils';
import { getHistory } from '../services/device-history.service';
import { Publish } from '../interfaces/publish';
import { awaitAll } from '../../utils/awaitAll';

const config = CONFIG();

export class EquipPusherComponent<P, S> extends Component<P, S> {
  constructor(props: P) {
    super(props);
    const getStore = get(window, 'SUPPORT.getStore', () => ({ store: {}, getState: () => 0 }))();
    this.store = get(getStore, 'store') as any;
  }

  store: Store | undefined;
  pusher: Pusher | undefined;
  channel: any;
  mounted = true;
  loading = false;

  unloadPusher = () => {
    this.channel && this.channel.unbind_all();
    this.pusher && this.pusher.disconnect();
    unset(this, 'channel');
    unset(this, 'pusher');
  }

  clearEquipMap = () => {
    //@ts-ignore
    this.store && this.store.dispatch && this.store.dispatch(clearEquipMap());
  }

  // AER-1608: wait for the channel's `pusher:subscription_succeeded` event so
  // that callers can rely on the channel actually receiving events before
  // they trigger device publishes. `pusher.subscribe()` is fire-and-forget —
  // the handshake to the Pusher server completes asynchronously (typically
  // 100–500ms after a fresh `new Pusher()`), and any `publishCreate_<id>`
  // events the backend fires during that gap are silently dropped on the
  // client side because the subscription isn't active yet. This is the root
  // cause of the "spinner spins forever on first selection but works after
  // re-selecting" symptom in AER-1608.
  //
  // Resolves once `subscription_succeeded` fires; falls back after 5s so a
  // genuinely-broken Pusher connection doesn't pin the spinner indefinitely.
  waitForSubscription = (channel: any): Promise<void> => {
    return new Promise(resolve => {
      if (!channel) { return resolve(); }
      // The channel exposes `subscribed` once the handshake is done — when
      // pusher-js reconnects an existing channel it's already true and the
      // event won't fire again, so check first.
      if (channel.subscribed) { return resolve(); }
      let done = false;
      const finish = () => {
        if (done) { return; }
        done = true;
        try { channel.unbind('pusher:subscription_succeeded', finish); } catch (e) { /* noop */ }
        try { channel.unbind('pusher:subscription_error', finish); } catch (e) { /* noop */ }
        resolve();
      };
      channel.bind('pusher:subscription_succeeded', finish);
      channel.bind('pusher:subscription_error', finish);
      setTimeout(finish, 5000);
    });
  }

  loadPusherChannel = async (_e: Equipment | Equipment[], _opts = {}) => {
    if (this.loading) { return }
    try {
      this.loading = true;
      const opts = {
        loadDetailedData: false,
        eventsToGet: [
          'dz_alert',
          'dz_sensor_statistics',
          'dz_sensor_instant',
          'dz_alert_data',
          'dz_profile_runtimes',
          'chair_sensor_statistics',
          'chair_sensor_instant',
          'chair_alert',
          'ster_cycle_end',
          'ster_sensor_instant',
          'ster_sensor_statistics',

          'ai_sensor_instant',
          'ai_sensor_statistics',
          'ai_alert_data',
          'ai_alert',

          //eventually remove these
          'iot_board/chair_sensor_statistics',
          'iot_board/chair_sensor_instant',
          'iot_board/chair_alert',
        ],
        onEvent: (d: any) => 0,
        ..._opts,
      }

      this.unloadPusher();

      this.pusher = new Pusher(config.pusher.key, {
        cluster: config.pusher.cluster,
        forceTLS: true,
      });

      this.channel = this.pusher.subscribe(config.pusherChannelName);

      const eqs = Array.isArray(_e) ? _e : [_e];

      // AER-1608: bind handlers FIRST (synchronously) before the subscription
      // handshake completes — pusher-js queues handlers so that any events
      // delivered the moment subscription succeeds are still routed.
      eqs.forEach(e => {
        this.channel && this.channel.bind(`publishCreate_${e.deviceId}`, (data: any) => {
          //@ts-ignore
          this.store && this.store.dispatch && this.store.dispatch(addToMap([data]));
          opts.onEvent && opts.onEvent(data);
        });
      });

      // AER-1608: hold here until the channel is actually subscribed so the
      // caller (retrieveLatestSensor) won't fire a device command into a
      // not-yet-listening client. Without this, on a cold ED mount the
      // device's publishCreate event lands ~300ms before the channel is
      // ready and is dropped — leaving the spinner stuck for 30s.
      await this.waitForSubscription(this.channel);

      const result = await Promise.all(eqs.map(async e => {
        if (opts.loadDetailedData) {
          const allDeviceIds = getAllDeviceIds(e);

          const allHistory = flatten(
            await Promise.all(allDeviceIds.map(async (ids) => {
              return !this.mounted ? [] : await awaitAll(opts.eventsToGet.map(ev => () => async () => await getHistory(ids.id, ev, ids.replaceDate)), { queueSize: 5 })
            }))
          );

          const flat = flatten(allHistory).map(p => ({
            ...p,
            coreid: get(allDeviceIds, [0, 'id']),
          })) as Publish[];

          if (this.mounted) {
            //@ts-ignore
            this.store && this.store.dispatch && this.store.dispatch(addToMap(flat, {
              clear: ['alerts', 'sensorStatistics']
            }));
          }

          return { data: flat }
        }
      }))

      return result;
    } catch (err) {
      console.log('Failed to channel', err);
    } finally {
      this.loading = false;
    }
  }

  unmount = () => {
    this.mounted = false;
    this.unloadPusher();
    this.clearEquipMap();
  }

  //ONLY WORKS IF YOUR COMPONENT DOESN"T OVERRIDE COMPONENTWILLUNMOUNT
  componentWillUnmount(): void {
    this.unmount();
  }
}